import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../api";
import { t } from "../i18n";
import { navigate } from "../utils/navigate";

function matchesMed(m, q) {
  if (!q) return true;
  const hay = `${m.brand} ${m.salt} ${m.strength} ${m.category}`.toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((part) => hay.includes(part));
}

export default function PatientHome({ lang }) {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [meds, setMeds] = useState([]);
  const [find, setFind] = useState("");
  const [adding, setAdding] = useState(false);
  const [copy, setCopy] = useState("");
  const [err, setErr] = useState("");

  const loadRx = () =>
    api("/api/prescriptions", { token }).then((data) => {
      setRows(data.prescriptions || []);
      setCopy(data.copy?.never_stop || "");
    });

  useEffect(() => {
    let live = true;
    loadRx()
      .catch((e) => live && setErr(e.message));
    api("/api/medicines", { token })
      .then((d) => live && setMeds(d.medicines || []))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [token]);

  const hits = useMemo(() => meds.filter((m) => matchesMed(m, find)).slice(0, 8), [meds, find]);

  const addPack = async (m) => {
    setErr("");
    setAdding(true);
    try {
      const res = await api("/api/prescriptions/home-pack", {
        method: "POST",
        token,
        body: { medicine_id: m.id },
      });
      setFind("");
      if (res.prescription?.id) navigate(`/rx/${res.prescription.id}`);
      else await loadRx();
    } catch (e) {
      setErr(e.message);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="tt-page">
      <h2>{t(lang, "myMedicines")}</h2>
      {err && <p className="tt-err">{err}</p>}
      {!rows.length && <p className="tt-muted">{t(lang, "noRx")}</p>}
      <div className="tt-list">
        {rows.map((rx) => (
          <button
            key={rx.id}
            className="tt-rx"
            onClick={() => navigate(`/rx/${rx.id}`)}
          >
            <strong>
              {rx.brand} · {rx.salt} {rx.strength}
            </strong>
            <span>
              {rx.dose} · {rx.days} {t(lang, "days").toLowerCase()}
            </span>
            {rx.origin === "family" && <span className="tt-chip">{t(lang, "familyLogged")}</span>}
            {rx.reason && rx.origin !== "family" && <span className="tt-muted">{rx.reason}</span>}
            {(rx.bad_reports > 0 || rx.unexpected_reports > 0) && (
              <span className="tt-chip red">{t(lang, "unexpected")}</span>
            )}
          </button>
        ))}
      </div>

      <h3>{t(lang, "addHomePack")}</h3>
      <p className="tt-muted">{t(lang, "homePackHint")}</p>
      <input
        placeholder={t(lang, "findPack")}
        value={find}
        onChange={(e) => setFind(e.target.value)}
        autoComplete="off"
        disabled={adding}
      />
      <div className="tt-suggest">
        {(find ? hits : meds.slice(0, 6)).map((m) => (
          <button key={m.id} type="button" disabled={adding} onClick={() => addPack(m)}>
            <strong>
              {m.brand} · {m.salt} {m.strength}
            </strong>
          </button>
        ))}
      </div>
      <p className="tt-foot">{copy || t(lang, "neverStop")}</p>
    </div>
  );
}
